import { Injectable, OnDestroy } from '@angular/core';
import {
  BehaviorSubject,
  Observable,
  Subject,
  Subscription,
  combineLatest,
  from,
  forkJoin,
  merge,
  of,
  timer,
} from 'rxjs';
import {
  catchError,
  exhaustMap,
  map,
  shareReplay,
  switchMap,
  takeWhile,
} from 'rxjs/operators';
import { MainRatesService } from './main-rates.service';
import {
  VerificacionMarkup,
  verificarMarkup,
} from '../components/main-rates/markup-check';

/**
 * Vigilancia del markup de la tasa Aguacate, para todo el sitio.
 *
 * El chip de la fila de tasas solo existe mientras el operador está parado en
 * el home. Este servicio hace la misma cuenta pero vive a nivel de aplicación,
 * así que sobrevive a los cambios de pantalla y el aviso alcanza al operador
 * donde esté.
 *
 * Lo arranca la barra de navegación, que es el componente que envuelve al
 * router-outlet, y solo para el rol operador.
 *
 * ## Por qué sondeo y no socket
 *
 * El backend refresca las tasas cada 3 minutos, así que un latido de 60 s ya
 * es tres veces más rápido que la fuente. Lo que sí importa —que el operador
 * que acaba de mover la tasa se entere en el acto— lo resuelve `revisarAhora`,
 * que se llama después de guardar.
 *
 * Si algún día el Nest emite el estado por socket, se reemplaza la fuente de
 * `estado$` y no hay que tocar ni el chip, ni el banner, ni el modal: todos
 * consumen observables y no saben de dónde sale el dato.
 *
 * ## Quién hace la cuenta
 *
 * El backend. Acá solo se arma el `VerificacionMarkup` con `verificarMarkup`,
 * usando el umbral que el backend informa — la misma función que ya estaba
 * probada, así que las dos puntas aplican la misma fórmula y no pueden
 * divergir. El silencio también manda el backend, y por eso ahora sobrevive
 * a una recarga.
 */

/** Latido de seguridad, para enterarse de tasas que movió otro operador. */
export const INTERVALO_VIGILANCIA_MS = 60_000;

/** Cuánto se calla el aviso después de que el operador lo reconoce. */
export const REAPARICION_AVISO_MS = 5 * 60_000;

export interface EstadoMarkup {
  verificacion: VerificacionMarkup;
  /** Silencio vigente de este operador, según el backend. */
  silencioHasta: Date | null;
  /** Intervalo configurado, para precargar el modal de configuración. */
  reaparicionSeg: number;
  /**
   * Operador activo de la tasa: BINANCE, MANUAL o CBPAY. Va en el aviso
   * porque el escenario 2 de la historia lo pide junto con los montos: sin él,
   * quien recibe el aviso no sabe qué fuente produjo la tasa desviada.
   */
  operador: string | null;
  actualizadoEn: Date;
}

export interface AvisoMarkup {
  verificacion: VerificacionMarkup;
  operador: string | null;
  /** Momento de la lectura. Va en el mensaje que el operador copia. */
  actualizadoEn: Date;
  /** Corresponde interrumpir al operador ahora. */
  mostrarModal: boolean;
  /** Segundos hasta que el aviso vuelva. 0 cuando no está en espera. */
  segundosParaReaparecer: number;
}

@Injectable({ providedIn: 'root' })
export class MarkupWatchService implements OnDestroy {
  private estadoSubject = new BehaviorSubject<EstadoMarkup | null>(null);
  /** Última lectura del markup. La consume el chip de la fila de tasas. */
  readonly estado$ = this.estadoSubject.asObservable();

  /** Momento hasta el cual el aviso queda en silencio. 0 = sin silencio. */
  private snoozeSubject = new BehaviorSubject<number>(0);

  /**
   * Qué corresponde mostrarle al operador: `null` mientras el markup esté
   * dentro de la banda, el modal cuando se pasó, y la cuenta regresiva
   * mientras esté reconocido.
   *
   * El reloj de un segundo corre **solo** durante la espera: fuera de eso no
   * hay emisiones, y por lo tanto no hay ciclos de detección de cambios de más.
   */
  readonly aviso$: Observable<AvisoMarkup | null> = combineLatest([
    this.estadoSubject,
    this.snoozeSubject,
  ]).pipe(
    switchMap(([estado, snoozeHasta]) => {
      const verificacion = estado?.verificacion;
      if (!verificacion || !verificacion.sobreUmbral) return of(null);
      const operador = estado?.operador ?? null;
      const actualizadoEn = estado?.actualizadoEn ?? new Date();

      if (snoozeHasta <= Date.now()) {
        return of({
          verificacion,
          operador,
          actualizadoEn,
          mostrarModal: true,
          segundosParaReaparecer: 0,
        });
      }

      return timer(0, 1000).pipe(
        map(() => Math.max(0, snoozeHasta - Date.now())),
        map((restanteMs) => ({
          verificacion,
          operador,
          actualizadoEn,
          mostrarModal: restanteMs === 0,
          segundosParaReaparecer: Math.ceil(restanteMs / 1000),
        })),
        // Al llegar a cero emite una última vez —ya con el modal— y el reloj
        // se apaga. Volver a reconocer arranca uno nuevo.
        takeWhile((aviso) => aviso.segundosParaReaparecer > 0, true),
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  private disparo$ = new Subject<void>();
  private sub?: Subscription;
  private onVolverAlTab?: () => void;

  constructor(private mainRates: MainRatesService) {}

  /** Idempotente: llamarlo dos veces no duplica el latido. */
  iniciar(): void {
    if (this.sub) return;

    this.sub = new Subscription();
    this.sub.add(
      merge(timer(0, INTERVALO_VIGILANCIA_MS), this.disparo$)
        // exhaustMap y no switchMap: si una lectura todavía está en vuelo, el
        // siguiente latido se descarta en vez de encimarse.
        .pipe(exhaustMap(() => this.leerEstado()))
        .subscribe((estado) => this.publicar(estado)),
    );

    // El operador deja la pestaña de fondo, la máquina suspende y el navegador
    // estrangula los timers. Al volver, lectura inmediata: es el mismo criterio
    // que ya usa el panel de tasas para no quedar mirando números viejos.
    this.onVolverAlTab = () => {
      if (!document.hidden) this.revisarAhora();
    };
    document.addEventListener('visibilitychange', this.onVolverAlTab);
  }

  detener(): void {
    this.sub?.unsubscribe();
    this.sub = undefined;
    if (this.onVolverAlTab) {
      document.removeEventListener('visibilitychange', this.onVolverAlTab);
      this.onVolverAlTab = undefined;
    }
  }

  /** Lectura inmediata. Se llama después de guardar una tasa. */
  revisarAhora(): void {
    this.disparo$.next();
  }

  /**
   * El operador dice que ya escaló. El aviso se calla y vuelve a los 5 minutos
   * si el markup sigue fuera de banda.
   *
   * En este paso el silencio vive en memoria: una recarga lo pierde. Cuando el
   * backend lo persista, este método pasa a escribirlo allá y el resto no
   * cambia.
   */
  reconocer(): void {
    const ratio = this.estadoSubject.value?.verificacion.ratio ?? 0;

    // Silencio optimista: el modal se cierra ya, sin esperar la red. Si el
    // POST falla, el próximo latido trae el silencio real del backend —que
    // será nulo— y el aviso vuelve. Preferimos eso a dejar al operador
    // mirando un modal congelado mientras viaja la petición.
    this.snoozeSubject.next(Date.now() + REAPARICION_AVISO_MS);

    this.mainRates.registrarAcuseMarkup(ratio).subscribe({
      next: (res) => {
        const hasta = res?.data?.silencioHasta;
        if (hasta) this.snoozeSubject.next(new Date(hasta).getTime());
      },
      error: () => {
        // El backend no registró el acuse. No lo escondemos: en el próximo
        // latido el silencio vuelve a cero y el aviso reaparece.
      },
    });
  }

  ngOnDestroy(): void {
    this.detener();
  }

  private publicar(estado: EstadoMarkup | null): void {
    // Lectura fallida: se conserva el estado anterior. Preferimos un aviso
    // desactualizado a que un corte de red lo haga desaparecer solo.
    if (!estado) return;

    this.estadoSubject.next(estado);

    // Volvió a la banda: se limpia el silencio, así que si vuelve a salirse
    // el aviso aparece de una y no hereda la espera anterior.
    if (!estado.verificacion.sobreUmbral) {
      if (this.snoozeSubject.value !== 0) this.snoozeSubject.next(0);
      return;
    }

    // El silencio lo manda el backend: así sobrevive a una recarga y es el
    // mismo aunque el operador tenga dos pestañas abiertas.
    const delBackend = estado.silencioHasta?.getTime() ?? 0;
    if (delBackend !== this.snoozeSubject.value) {
      this.snoozeSubject.next(delBackend);
    }
  }

  /**
   * Las dos lecturas van en paralelo y cada una se protege sola: si falla
   * cualquiera, no hay verificación posible y se devuelve `null`.
   */
  private leerEstado(): Observable<EstadoMarkup | null> {
    return this.mainRates.obtenerEstadoMarkup().pipe(
      map((res) => {
        const d = res?.data;
        if (!d) return null;

        // Se recalcula con la misma función probada, pasándole el umbral que
        // informa el backend. No es desconfianza: es que el chip necesita la
        // holgura en pesos, que el endpoint no devuelve, y así las dos puntas
        // quedan atadas a una sola fórmula.
        const verificacion = verificarMarkup(d.spot, d.aguacate, d.umbral);
        if (!verificacion) return null;

        return {
          verificacion,
          reaparicionSeg: Number(d.reaparicionSeg) || REAPARICION_AVISO_MS / 1000,
          operador: d.operador ?? null,
          actualizadoEn: d.actualizadoEn ? new Date(d.actualizadoEn) : new Date(),
          silencioHasta: d.silencioHasta ? new Date(d.silencioHasta) : null,
        };
      }),
      catchError(() => of(null)),
    );
  }
}
